import { useCallback, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { isNil } from 'lodash-es';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { PaginatedBody, SearchQuery, SortOrderQuery, UseQueryConfig } from '@/api/types';
import { SelectItemType } from '@/types';
import { useFilters, UseFiltersType } from '@/hooks/useFilters';

const defaultMapper = <T extends Record<string, unknown>, R extends Record<string, unknown>>(item: T): R =>
  ({
    value: item.id,
    name: item.name,
  }) as unknown as R;

export type UseAsyncSearchableListType<R, F> = {
  list: R[];
  isLoading: boolean;
  isFirstLoading: boolean;
  hasNextPage: boolean;
  searchValue: string;
  count: number;
  loadNextPage: () => void;
  setSearchValue: (text: string) => void;
} & UseFiltersType<F>;

type UseAsyncSearchableListProps<T, R, F> = {
  query: (
    filters: SearchQuery & SortOrderQuery & Partial<F>,
    options?: UseQueryConfig,
  ) => UseInfiniteQueryResult<InfiniteData<PaginatedBody<T>, unknown>, Error>;
  mapper?: (item: T) => R;
  defaultSortOrder?: SortOrderQuery;
  defaultFilters?: F;
  defaultSearchValue?: string;
  formatSearchValue?: (value: string) => string;
};

export const useAsyncSearchableList = <
  T extends Record<string, unknown>,
  R extends Record<string, unknown> = SelectItemType,
  F extends Record<string, unknown> = Record<string, unknown>,
>({
  query,
  mapper = defaultMapper,
  defaultSortOrder = {},
  defaultFilters = {} as F,
  defaultSearchValue = '',
  formatSearchValue,
}: UseAsyncSearchableListProps<T, R, F>): UseAsyncSearchableListType<R, F> => {
  const defaultSortOrderRef = useRef<SortOrderQuery>(defaultSortOrder);
  const { filters, hasFilters, updateFilters, clearFilters } = useFilters(defaultFilters);
  const [searchValue, setSearchValue] = useState<string>(defaultSearchValue);
  const [debouncedFilterValue, setDebouncedFilterValue] = useState<SearchQuery & SortOrderQuery & Partial<F>>({
    ...defaultFilters,
    ...defaultSortOrder,
  });
  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = query(debouncedFilterValue, {
    enabled: !!Object.keys(debouncedFilterValue).length,
  });

  useDebounce(
    () => {
      const appliedFilters = Object.keys(filters).reduce((acc, key) => {
        const filterKey = key as keyof F;

        if (Array.isArray(filters[filterKey])) {
          const array = filters[filterKey] as unknown[];

          if (array.length) {
            return {
              ...acc,
              [key]: array.map((i) => String(i)).join(','),
            };
          }
        }

        if (!isNil(filters[filterKey])) {
          return {
            ...acc,
            [key]: filters[filterKey],
          };
        }

        return acc;
      }, {} as Partial<F>);

      const cleanSearchValue = formatSearchValue?.(searchValue) ?? searchValue;

      setDebouncedFilterValue({
        searchQuery: cleanSearchValue || undefined,
        ...defaultSortOrderRef.current,
        ...appliedFilters,
      });
    },
    500,
    [searchValue, filters],
  );

  const list = useMemo<R[]>(() => {
    if (!data || !data?.pages) return [];
    return data.pages.map((page) => page.items?.map(mapper) || []).flat();
  }, [data, mapper]);

  const loadNextPage = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  return {
    list,
    isFirstLoading: isLoading,
    isLoading: isLoading || isFetchingNextPage,
    count: data?.pages[0]?.totalItems || 0,
    hasNextPage,
    loadNextPage,
    searchValue,
    setSearchValue,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
  };
};
